/**
 * The one place an artist is scored against the standard other selling artists meet.
 *
 * `profile-completeness.ts` answers "how finished is your profile". This answers a
 * bigger and more useful question — "are you operating like an artist who sells" — and
 * demotes the profile to one pillar of four. It does NOT re-derive the profile score:
 * it calls `profileCompleteness()`, because that module is explicitly the one place a
 * profile is scored and an artist must never be shown two different profile numbers.
 *
 * EVERY BAR HERE IS MEASURED, NOT INVENTED. The whole point of a normative frame — "be
 * like other artists who sell their work" — is that the claim is checkable, so a made-up
 * threshold would quietly destroy the only thing that makes this stronger than a
 * completeness percentage. Measured 2026-08-29 against production, all-time, users with
 * at least one upload, ASF team and persona bots excluded. Sellers (n=13,929) are ASF
 * customers, who run e-commerce art storefronts; cold non-ASF (n=52,424) is the
 * comparison:
 *
 *   pieces held          sellers median 12   ·  cold median 2     (6x)
 *   ever saved a mockup  sellers 35.0%       ·  cold 12.1%        (2.9x)
 *   connected a channel  sellers 22.3%       ·  cold 3.5%         (6.4x)
 *   profile score        sellers 29          ·  cold 26           (1.1x)
 *
 * Three of those readings shaped the design and are worth keeping written down:
 *
 * 1. The body-of-work bar is TWELVE, not a round ten. The median is 12, so the bar is 12.
 *
 * 2. Titles and descriptions are deliberately absent. They are 96%+ complete for cold
 *    users because AI fills them at upload — HIGHER than sellers' own 65-68% — so a
 *    "match what sellers do" bar on them would be a bar our users already clear. Putting
 *    a solved field in a meter is how meters get ignored. The real artwork gap is mockups.
 *
 * 3. The 3-days-a-week posting cadence is NOT a measured norm and is not presented as
 *    one. Nine ASF sellers out of 13,929 clear it. It is an aspiration — carried at a
 *    deliberately small weight, and flagged `aspirational` so the UI can say so out loud
 *    rather than implying other artists are doing something they are not.
 *
 * Weights sum to exactly 100, so the score IS the sum of the earned weights and no
 * normalisation step can drift — the same property `profile-completeness.ts` asserts.
 */

import { profileCompleteness, type CompletenessProfile } from './profile-completeness';

/** The measured median body of work among artists who sell. The bar, not a round number. */
/**
 * Art Review is a SEPARATE APP on its own subdomain, so these are absolute URLs and the
 * steps that use them open in a new tab — sending her out of Town Square in the same tab
 * would throw away whatever she was doing, and she has to come back here afterwards.
 *
 * The `surprise-me` entry points pick a piece for her. That is deliberate: the step exists
 * because she does not know which piece to start with, so asking her to choose one first
 * is the same barrier wearing a different hat.
 *
 * NOTE ON THE LINKS: the pricing URL was handed over carrying `_gl`, `_ga` and `_gcl_au`
 * query parameters. Those are Google Analytics cross-domain linker values from ONE
 * browser session — a client id and a time-limited, hash-validated handoff token. Shipped,
 * they would stamp every artist's visit with the same identity and would expire besides.
 * The canonical paths are stored here; the linker adds its own params at click time.
 */
const ART_REVIEW_HOME = 'https://artreview.arthelper.com';
export const ART_REVIEW_URL = `${ART_REVIEW_HOME}/review-1/surprise-me`;
export const ART_PRICING_URL = `${ART_REVIEW_HOME}/pricing-1/surprise-me`;

/**
 * The room-mockup generator — its own app on its own subdomain, so it opens in a new tab
 * like Art Review and pricing. Verified live (200). Both the selling band and the
 * getting-seen band point here: it is the same generator either way.
 */
const MOCKUPS_HOME = 'https://mockups.arthelper.com';
export const MOCKUPS_URL = `${MOCKUPS_HOME}/`;

/** Art Review on one specific piece, rather than the `surprise-me` picker. */
export function artReviewPathFor(imageId: string | number): string {
  return `/review-1/${encodeURIComponent(String(imageId))}`;
}

export function artReviewUrlFor(imageId: string | number): string {
  return `${ART_REVIEW_HOME}${artReviewPathFor(imageId)}`;
}

/** The mockup generator opened on one specific piece. */
export function mockupsPathFor(imageId: string | number): string {
  return `/i/${encodeURIComponent(String(imageId))}`;
}

export function mockupsUrlFor(imageId: string | number): string {
  return `${MOCKUPS_HOME}${mockupsPathFor(imageId)}`;
}

/**
 * The Ideal Collector sparklink — spark 380, VERBATIM from the broadcast we actually sent.
 *
 * Found in `ops/state/email-studio/drafts/draft-2026-08-15-ideal-collector.json` in the
 * agent-os repo: sent 2026-08-15 to ~76k in two waves. The message is copied exactly, so
 * an artist arriving from the meter gets the same Arty conversation as an artist arriving
 * from the email, and the two are comparable.
 *
 * Community is the default surface for every sparklink (CEO, 2026-08-03), which is what
 * this carries. The upload-first variant of this link exists for zero-upload recipients;
 * this step requires a piece instead, so it uses the plain one.
 */
export const IDEAL_COLLECTOR_SPARK_ID = 380;
export const IDEAL_COLLECTOR_URL =
  'https://www.arthelper.com/community?spark_id=380&message=' +
  'Look%20at%20all%20my%20work%20and%20tell%20me%20exactly%20who%20my%20ideal%20collector%20is%20-%20' +
  'who%20they%20are%2C%20what%20is%20already%20hanging%20on%20their%20walls%2C%20and%20where%20they%20' +
  'are%20most%20likely%20to%20run%20into%20my%20work.';

export const BODY_OF_WORK_BAR = 12;

/** The aspirational posting cadence, in days per week. See note 3 above. */
export const POSTING_CADENCE_BAR = 3;

/**
 * Pillar and step keys are open strings, not a closed union, because each intent variant
 * brings its OWN pillars — that is what "different pillars per intent" means. A union
 * would have to be the concatenation of all four variants' keys, which reads as though any
 * variant might produce any pillar, and no consumer could rely on it anyway.
 */
export type ArtistStandardPillarKey = string;

export type ArtistStandardStepKey = string;

/**
 * Everything the score reads. Every field optional: each caller hands over the subset it
 * already loaded, and an absent field simply scores as zero rather than throwing.
 */
export type ArtistStandardSubject = {
  /** Pieces in the artist's library. */
  imageCount?: number | null;
  /** Pieces that have been through Art Review. */
  reviewedCount?: number | null;
  /** Pieces that have been through Price My Art. */
  pricedCount?: number | null;
  /** Pieces carrying at least one saved mockup. */
  mockupCount?: number | null;
  /** Social/storefront channels connected. */
  connectedChannelCount?: number | null;
  /** Days per week the artist currently posts. */
  postingDaysPerWeek?: number | null;
  /** Passed straight through to `profileCompleteness()`. */
  profile?: CompletenessProfile | null;

  // --- read by the non-selling variants ---
  // Every one of these is a real column, deliberately: a variant whose steps cannot be
  // measured is a mock-up, and it would ship a bar that never moves.

  /** Posts the artist has made in a community (`community_posts`). */
  communityPostCount?: number | null;
  /**
   * Posts that count as asking for critique. The welcome-wizard intro is not one of
   * them; a one-click critique is. Same reading as the quest step of this name.
   */
  postForCritiqueCount?: number | null;
  /** Comments the artist has left on OTHER artists' work (`community_comments`). */
  commentsGivenCount?: number | null;
  /** Artists followed (`follows`). */
  followingCount?: number | null;
  /** Pieces carrying both a title and a description (`images.title`, `images.description`). */
  describedCount?: number | null;
  /** Pieces carrying artwork specs (`images.specsMeta`). */
  specsCount?: number | null;
  /** Collections the artist has made (`collections`). */
  collectionCount?: number | null;
  /**
   * Ideal Collector runs completed — `completions` where `completionTypeId` = 380.
   * Verified against production: 264 completions across 78 users.
   */
  idealCollectorCount?: number | null;
};

export type ArtistStandardStep = {
  key: ArtistStandardStepKey;
  pillar: ArtistStandardPillarKey;
  weight: number;
  /** Earned weight, 0..weight. */
  earned: number;
  complete: boolean;
  /**
   * The action that closes this step, and WHY it is worth doing, in one line.
   *
   * The reason is IN the label, not beside it. Nick, 2026-08-30: *"we need to sell the
   * steps better. These are very generic."* "Upload 12 pieces" is an instruction from us;
   * "Add more artwork — 6 of 12, enough to show a body of work instead of a sample" is a
   * reason for her. A step an artist has to be told twice to care about was written once,
   * badly.
   */
  label: string;
  /**
   * The longer sell, shown when the step opens in place. Two or three sentences: what she
   * gets, and the number behind the bar where there is one.
   */
  why: string;
  /**
   * What other selling artists do, in the artist's own words. This is the argument, so
   * it is data rather than UI copy — a surface that shows the step must be able to show
   * why the bar is where it is.
   */
  evidence: string;
  /** Where the artist goes to close it. */
  href: string;
  /**
   * HOW the step opens. The module owns this rather than the component, because it is a
   * property of the step — whether the thing that closes it can happen where she is
   * standing — not of the surface showing it.
   *
   * - `artwork-modal` — the real upload dialog, mounted in place. Nothing to navigate to:
   *   adding work IS a modal, and sending her to a gallery page to find the same button
   *   is a detour that also closes the list she opened.
   * - `new-tab` — a real page she should keep, opened beside what she is doing. Her own
   *   profile is the case: she has to look at it while she fixes it, and a same-tab
   *   navigation would throw away wherever she came from.
   * - `explainer` — the sell, for a step with no in-place surface built yet. Honest
   *   placeholder, and the only one of the three the prototype invented.
   */
  opens: 'artwork-modal' | 'new-tab' | 'explainer';
  /** True when the bar is a product goal rather than a measured norm. See note 3. */
  aspirational?: boolean;
  /**
   * Roughly how long it takes, in minutes, rendered as "(takes 1 min)".
   *
   * Only on steps that genuinely finish in one sitting. The commitment steps — build a
   * body of work, post three days a week — carry NO estimate, because there is no honest
   * number for them and an invented one is found out within the minute.
   */
  minutes?: number;
  /**
   * Whether she can start this step AT ALL yet.
   *
   * The order is easy-first, and three of the easy steps operate on a piece she has
   * already uploaded — a review, a price and a mockup all need artwork. Without this a
   * brand-new artist would be told to "get an Art Review" as her first action and land on
   * a page with nothing to review. A blocked step is skipped when choosing the NEXT
   * action; it is still listed and still scored, because it is still part of the standard.
   */
  requires?: (subject: ArtistStandardSubject) => boolean;
};

export type ArtistStandardPillar = {
  key: ArtistStandardPillarKey;
  label: string;
  weight: number;
  earned: number;
  complete: boolean;
  steps: ArtistStandardStep[];
};

/**
 * Ladder A. The top rung IS the standard, which is why the rung name and the
 * disappear-forever condition are the same event.
 */
export const RUNGS_STANDARD = ['Starting Out', 'Building', 'Nearly There', 'Meets the Artist Standard'] as const;

/** Ladder B — the career-stage naming, rendered beside A on the prototype for comparison. */
export const RUNGS_CAREER = ['Getting Started', 'Emerging', 'Established', 'Strong', 'Professional'] as const;

export type RungLadder = 'standard' | 'career';

/**
 * Lower bound of each rung, index-aligned to the ladders above. A rung is the last band
 * whose floor the score has reached.
 */
export const RUNG_FLOORS: Record<RungLadder, readonly number[]> = {
  standard: [0, 25, 60, 100],
  career: [0, 25, 50, 75, 100],
};

const RUNG_NAMES: Record<RungLadder, readonly string[]> = {
  standard: RUNGS_STANDARD,
  career: RUNGS_CAREER,
};

/**
 * The highest rung index reachable while Body of Work is incomplete.
 *
 * A weight alone would not deliver "nobody reaches a high rung on profile completeness
 * alone": profile + mockups + a channel connect is 55 points, which lands in the third
 * band with two pieces of art. The cap is what makes it a rule instead of a preference,
 * and it is deliberately the SAME index on both ladders so the gate cannot be escaped by
 * renaming the rungs.
 */
const RUNG_CAP_WITHOUT_GATE = 1;

/** Fraction of a bar that has been met, clamped to 0..1 and safe on a zero bar. */
function ratio(value: number | null | undefined, bar: number): number {
  if (!bar || bar <= 0) return 0;
  const n = typeof value === 'number' && Number.isFinite(value) ? value : 0;
  if (n <= 0) return 0;
  return Math.min(1, n / bar);
}

/**
 * Coverage of the artist's OWN pieces — "every piece has what a buyer needs".
 *
 * Deliberately not measured against the 12-piece bar: how MANY pieces you hold is pillar
 * 1's job, and charging the same shortfall twice would make a five-piece artist look
 * twice as far behind as they are.
 */
function coverage(value: number | null | undefined, imageCount: number | null | undefined): number {
  const total = typeof imageCount === 'number' && Number.isFinite(imageCount) ? imageCount : 0;
  if (total <= 0) return 0;
  return ratio(value, total);
}

type StepSpec = Omit<ArtistStandardStep, 'earned' | 'complete' | 'label'> & {
  /**
   * A function, not a string, so a step can count out loud — "6 of 12" tells her how far
   * she has come and what is left in the same breath, which a fixed "Upload 12 pieces"
   * cannot. The Co-pilot's artwork rung does the same and for the same reason.
   */
  label: (subject: ArtistStandardSubject) => string;
  progress: (subject: ArtistStandardSubject) => number;
};

/**
 * Declaration order IS the tie-break order for "the single next action", so equal-weight
 * steps resolve deterministically rather than by object-key iteration order.
 *
 * The order is the GUIDED PATH, not the weight ranking. They nearly coincide here — the
 * heaviest step is also the first thing to do — but where they diverge the path wins, and
 * the posting cadence is the case: it is the step an artist cannot act on until they have
 * work and a channel, so it sits last despite being the one the business wants most. The
 * Co-pilot's exposure meter reordered for exactly this reason; a weight-sorted list tells
 * a brand-new artist to post three days a week before they have anything to post.
 */
/**
 * NEVER MEASURE HER AGAINST THE ARTISTS AROUND HER. Nick, 2026-08-30, on
 * "The typical artist here holds 2": *"we should never say that — no reference to the
 * typical artist."*
 *
 * The comparison that motivates is upward, to artists who SELL. The comparison to the
 * median artist here is downward, and it does two bad things at once: it tells her the bar
 * is lower than we just said it was, and it says out loud that most people on this product
 * are failing. "35% of artists who sell have made a mockup" is an invitation. "Across
 * everyone else it is 12%" is a report card on everybody, including her. Held by
 * `no downward comparisons` in the spec.
 */
/**
 * THE ORDER IS EASY-FIRST, AND THE COMMITMENT SITS IN THE MIDDLE.
 *
 * Nick, 2026-08-31: *"'add more artwork' should come before 'connect a channel'. It's a
 * bigger commitment step we don't need yet. Everything before it should be easy."*
 *
 * So the first four finish in a sitting — a review, a price, a mockup, a profile — and
 * each says how long it takes. Building a body of work is the real ask, and it earns its
 * place only after we have given her something back. Connecting a channel and posting
 * three days a week stay behind it, in his order.
 *
 * This is what `requires` exists for: three of those four easy steps operate on a piece
 * she has already uploaded, so they are skipped when choosing the next action until she
 * has one — at which point the next action falls through to adding artwork, the only
 * thing she can actually do.
 */
const SELL_STEPS: readonly StepSpec[] = [
  {
    key: 'reviewed',
    pillar: 'bodyOfWork',
    weight: 10,
    label: () => 'Get an Art Review — find out which pieces to lead with',
    evidence: 'A review tells you which pieces are ready to sell before a buyer decides for you.',
    why: 'You know your own work too well to see it cold. An Art Review reads a piece back to you the way someone meeting it for the first time would, so the strongest ones go first instead of whatever you happened to upload last.',
    minutes: 1,
    requires: (s) => (s.imageCount ?? 0) > 0,
    href: ART_REVIEW_URL,
    opens: 'new-tab',
    progress: (s) => coverage(s.reviewedCount, s.imageCount),
  },
  {
    key: 'priced',
    pillar: 'bodyOfWork',
    weight: 10,
    label: () => 'Get a pricing analysis — see what your work is worth',
    evidence: 'Pricing used to be behind a paywall. It is free now, and unpriced work cannot sell.',
    why: 'Pricing your own work is the thing artists say they find hardest, and guessing low is the expensive mistake. A pricing analysis gives you a number to argue with instead of a blank field — and work with no price on it reads to a buyer as work that is not for sale.',
    minutes: 1,
    requires: (s) => (s.imageCount ?? 0) > 0,
    href: ART_PRICING_URL,
    opens: 'new-tab',
    progress: (s) => coverage(s.pricedCount, s.imageCount),
  },
  {
    key: 'idealCollector',
    pillar: 'exposure',
    weight: 5,
    label: () => 'Know your ideal collector — who buys work like yours, and where they are',
    evidence: 'Arty reads your whole body of work and tells you who it is for.',
    why: 'Everything downstream of this gets easier once you can picture the person: which pieces to lead with, what to write under them, and which channel is worth your time. Guessing at "everyone" is why most art marketing goes nowhere.',
    minutes: 1,
    requires: (s) => (s.imageCount ?? 0) > 0,
    href: IDEAL_COLLECTOR_URL,
    opens: 'new-tab',
    progress: (s) => ratio(s.idealCollectorCount, 1),
  },
  {
    key: 'mockups',
    pillar: 'artworkCompleteness',
    weight: 20,
    label: () => 'Show a piece on a wall — nobody can judge size from a crop',
    evidence: '35% of artists who sell have made a mockup of their work.',
    why: 'A photograph of a canvas tells a buyer nothing about how big it is or what it would look like above their sofa. One mockup answers both questions in a single image, without you photographing anything twice.',
    href: MOCKUPS_URL,
    opens: 'new-tab',
    minutes: 1,
    requires: (s) => (s.imageCount ?? 0) > 0,
    progress: (s) => coverage(s.mockupCount, s.imageCount),
  },
  {
    key: 'profileFields',
    pillar: 'profile',
    weight: 15,
    label: () => 'Finish your profile — it is the page a buyer lands on',
    evidence: 'Your profile is the page a buyer lands on. It is worth finishing, but it is not what sells work.',
    why: 'When someone finds your work, this is where they go to decide whether you are a real artist. It is worth finishing — and it is the smallest part of this score on purpose, because a perfect profile with two pieces behind it still sells nothing.',
    href: '/profile',
    opens: 'new-tab',
    minutes: 5,
    progress: (s) => profileCompleteness(s.profile).score / 100,
  },
  {
    key: 'pieces',
    pillar: 'bodyOfWork',
    weight: 25,
    label: (s) => {
      const count = Math.min(s.imageCount ?? 0, BODY_OF_WORK_BAR);
      return `Add more artwork — ${count} of ${BODY_OF_WORK_BAR}, enough to show a body of work instead of a sample`;
    },
    evidence: `Artists who sell their work hold ${BODY_OF_WORK_BAR} pieces.`,
    why: `A buyer deciding whether to take you seriously counts what is there. ${BODY_OF_WORK_BAR} is also enough to post for a month without repeating yourself, which is why the Co-pilot lands on the same number from the other direction.`,
    href: '/my-artwork',
    opens: 'artwork-modal',
    progress: (s) => ratio(s.imageCount, BODY_OF_WORK_BAR),
  },
  {
    key: 'connectChannel',
    pillar: 'exposure',
    weight: 10,
    label: () => 'Connect a social media channel — work nobody sees does not sell',
    evidence: '22% of artists who sell have connected at least one channel.',
    why: 'Your gallery is one page that nobody arrives at by accident. Connecting a single account is the difference between work that exists and work that gets in front of the people who might buy it.',
    href: '/integrations',
    opens: 'explainer',
    minutes: 2,
    progress: (s) => ratio(s.connectedChannelCount, 1),
  },
  {
    key: 'postingCadence',
    pillar: 'exposure',
    weight: 5,
    label: () => `Post ${POSTING_CADENCE_BAR} days a week — automated exposure, without doing it by hand`,
    // Named as a goal, out loud. Four artists in 12,900 manage this by hand, so claiming
    // it as a norm would be the exact fabrication this module exists to avoid.
    evidence: `Almost nobody keeps this up by hand. It is a goal rather than a measured norm, and it is what automation is for.`,
    why: `This is the one bar here we set rather than measured, and it is the one you should not try to hit by hand. It is a time problem, not a discipline problem — which is exactly the job the Co-pilot exists to take off you.`,
    href: '/calendar',
    opens: 'explainer',
    aspirational: true,
    progress: (s) => ratio(s.postingDaysPerWeek, POSTING_CADENCE_BAR),
  },
];

/**
 * A pillar row NAMES THE PAYOFF, not the category. Nick rewrote all four himself on
 * 2026-08-30 to show what he meant: "Body of Work" is a filing label, "Upload 12+ pieces
 * to improve presence and advice received" is a reason to press it. The same rule the
 * steps got — the sell belongs IN the label, because the label is the only part most
 * artists will ever read.
 *
 * Verbatim his words. `BODY_OF_WORK_BAR` is interpolated so the number cannot drift away
 * from the bar the score is actually computed against.
 */
const SELL_PILLAR_LABELS: Record<ArtistStandardPillarKey, string> = {
  bodyOfWork: `Upload ${BODY_OF_WORK_BAR}+ pieces to improve presence and advice received`,
  profile: 'Strengthen your online profile to attract collectors and opportunities',
  artworkCompleteness: 'Artwork Completeness: fill-in the common details of your art',
  exposure: 'Automated Exposure - get eyeballs to see your art',
};

/** Pillar display order — Nick's order, which is not the weight order. */
const SELL_PILLAR_ORDER: readonly ArtistStandardPillarKey[] = [
  'bodyOfWork',
  'profile',
  'artworkCompleteness',
  'exposure',
];

/** Total weight carried by each pillar. Derived, so it can never disagree with STEPS. */
/**
 * ============================================================================
 * INTENT VARIANTS
 * ============================================================================
 *
 * 19,568 artists have answered PRIMARY_INTENT and it is MULTI-SELECT — 2.45 picks each.
 * 62.6% chose "Sell my art" and are served by the default. The other 7,320 did not choose
 * it at all, and the default band pitches pricing and sales channels at every one of them.
 * That is the whole reason these exist.
 *
 * Each variant carries its OWN pillars and steps, not a re-weighting of one set: an artist
 * who came to get feedback is not a worse version of an artist who came to sell, and
 * scoring her against a storefront she never asked for would be the meter lying about her.
 *
 * WEIGHTS SUM TO 100 INSIDE EVERY VARIANT, so the score is always the sum of earned
 * weights and no normalisation step can drift. Asserted per variant in the spec.
 *
 * The scores are NOT comparable across variants and nothing should ever try — two artists
 * on different variants are being measured against different standards, which is the
 * point. It is also why the variant travels on the result.
 */
export type IntentVariantKey = 'sell' | 'seen' | 'better' | 'organize';

export type IntentVariant = {
  key: IntentVariantKey;
  /** The band's headline. */
  heading: string;
  /** Which pillar must be complete before the top rung opens. */
  gatePillar: ArtistStandardPillarKey;
  /**
   * The requirement the gate is holding the top rung for, in the artist's words.
   * Sell-specific ("12 pieces, reviewed and priced") is wrong on every other variant.
   */
  gateRequirement: string;
  pillarLabels: Record<ArtistStandardPillarKey, string>;
  pillarOrder: readonly ArtistStandardPillarKey[];
  steps: readonly StepSpec[];
};

/**
 * "GETTING SEEN" — the 1,744 non-sellers who picked "Help with social media", and the
 * largest single intent after selling. The pieces bar is the same 12, arrived at from the
 * other direction: 12 is what lets her post three days a week for a month without
 * repeating herself.
 */
const SEEN_STEPS: readonly StepSpec[] = [
  {
    key: 'pieces',
    pillar: 'enoughToPost',
    weight: 25,
    label: (s) =>
      `Add more artwork — ${Math.min(s.imageCount ?? 0, BODY_OF_WORK_BAR)} of ${BODY_OF_WORK_BAR}, a month of posts without repeating yourself`,
    evidence: `Posting ${POSTING_CADENCE_BAR} days a week for a month takes ${BODY_OF_WORK_BAR} pieces.`,
    why: `Running out of things to post is what ends most posting streaks. ${BODY_OF_WORK_BAR} pieces gives you four weeks of ${POSTING_CADENCE_BAR}-day weeks before anyone sees the same work twice, so your feed keeps going without you scrambling for something to put in it.`,
    href: '/my-artwork',
    opens: 'artwork-modal',
    progress: (s) => ratio(s.imageCount, BODY_OF_WORK_BAR),
  },
  {
    key: 'mockups',
    pillar: 'postReady',
    weight: 25,
    label: () => 'Make a room mockup — a flat photo of a canvas scrolls past',
    evidence: '35% of artists who sell have made a mockup of their work.',
    why: 'A feed is a wall of rectangles and a flat photograph of your painting is one more of them. Your piece hanging in a room reads as an object someone could own, which is what makes a thumb stop scrolling.',
    href: MOCKUPS_URL,
    opens: 'new-tab',
    minutes: 1,
    requires: (s) => (s.imageCount ?? 0) > 0,
    progress: (s) => coverage(s.mockupCount, s.imageCount),
  },
  {
    key: 'connectChannel',
    pillar: 'channels',
    weight: 30,
    label: () => 'Connect a social media channel — this is the whole thing',
    evidence: '22% of artists who sell have connected at least one channel.',
    why: 'Everything else here is preparation. Until an account is connected, none of it reaches anyone outside this site, and the work stays exactly as seen as it was before you started.',
    href: '/integrations',
    opens: 'explainer',
    minutes: 2,
    progress: (s) => ratio(s.connectedChannelCount, 1),
  },
  {
    key: 'postingCadence',
    pillar: 'cadence',
    weight: 20,
    aspirational: true,
    label: () => `Post ${POSTING_CADENCE_BAR} days a week — automated exposure, without doing it by hand`,
    evidence:
      'Almost nobody keeps this up by hand. It is a goal rather than a measured norm, and it is what automation is for.',
    why: 'This is the one bar here we set rather than measured, and the one you should not try to hit manually. It is a time problem, not a discipline problem — exactly the job the Co-pilot exists to take off you.',
    href: '/calendar',
    opens: 'explainer',
    progress: (s) => ratio(s.postingDaysPerWeek, POSTING_CADENCE_BAR),
  },
];

/**
 * "GETTING BETTER" — feedback, connect, learn and inspire together: 6,426 non-sellers who
 * all want the same thing, other people's eyes on the work. Its gate is ASKING, because a
 * critique community cannot give feedback to someone who never posted.
 */
const BETTER_STEPS: readonly StepSpec[] = [
  {
    key: 'pieces',
    pillar: 'workToShow',
    weight: 20,
    label: (s) => `Add work to show — ${Math.min(s.imageCount ?? 0, 5)} of 5, enough that a critique sees a direction`,
    evidence: 'A reviewer reading five pieces can see what you are working toward. One piece only gets you a reaction.',
    why: 'Feedback on a single image is feedback on that image. Once there are a few, people start telling you what your work is actually about — which is the part you cannot see from the inside.',
    href: '/my-artwork',
    opens: 'artwork-modal',
    progress: (s) => ratio(s.imageCount, 5),
  },
  {
    key: 'postForCritique',
    pillar: 'askForFeedback',
    weight: 30,
    label: () => 'Post a piece and ask — nobody critiques work they have not been shown',
    evidence: 'Feedback starts with a post. There is no queue you can join by waiting.',
    why: 'This is the step everything else here is for. Artists who post a piece and say what they want looked at get real answers; your work sitting quietly in a gallery gets scrolled past, however good it is.',
    href: '/community',
    opens: 'explainer',
    progress: (s) => ratio(s.postForCritiqueCount, 1),
  },
  {
    key: 'commentOnOthers',
    pillar: 'giveFeedback',
    weight: 25,
    label: (s) => `Critique someone else's work — ${Math.min(s.commentsGivenCount ?? 0, 5)} of 5`,
    evidence:
      'Critique communities run on reciprocity. The artists who get the most feedback are the ones who give it.',
    why: "Leaving a real comment on someone else's piece is the fastest way to be seen by the people whose opinion you want — and reading other work closely is where most of the learning actually happens.",
    href: '/community',
    opens: 'explainer',
    minutes: 2,
    progress: (s) => ratio(s.commentsGivenCount, 5),
  },
  {
    key: 'followArtists',
    pillar: 'yourPeople',
    weight: 25,
    label: (s) => `Follow artists working near you — ${Math.min(s.followingCount ?? 0, 10)} of 10`,
    evidence: 'A feed of work like yours is where useful feedback and useful ideas both come from.',
    why: 'Who you follow decides what you see, and an empty feed gives you nothing to react to and nobody to be seen by. Ten is roughly where a feed stops being empty and starts being yours.',
    href: '/artists',
    opens: 'explainer',
    minutes: 2,
    progress: (s) => ratio(s.followingCount, 10),
  },
];

/**
 * "AN ORGANISED PORTFOLIO" — the 881 non-sellers who picked "Organize my portfolio". The
 * only variant where the artwork DETAIL work is the point rather than the housekeeping,
 * which is why it is gated on it.
 */
const ORGANIZE_STEPS: readonly StepSpec[] = [
  {
    key: 'pieces',
    pillar: 'everythingUploaded',
    weight: 25,
    label: (s) => `Get your work in one place — ${s.imageCount ?? 0} uploaded so far`,
    evidence: 'A portfolio you have to assemble from three folders and a phone is one you will not use.',
    why: 'The value of organising shows up only once everything is in the same place. Until then you are keeping two systems, and the one on your laptop always wins.',
    href: '/my-artwork',
    opens: 'artwork-modal',
    progress: (s) => ratio(s.imageCount, BODY_OF_WORK_BAR),
  },
  {
    key: 'described',
    pillar: 'everyPieceDescribed',
    weight: 20,
    label: () => 'Title and describe each piece — so you can find it again',
    evidence: 'A titled, described piece is one you can search for. An untitled one you have to scroll for.',
    why: 'Titles and descriptions are what make a portfolio searchable by you, not just readable by a visitor. They are also what every other surface — a listing, a post, a submission — pulls from later.',
    href: '/art',
    opens: 'explainer',
    minutes: 5,
    requires: (s) => (s.imageCount ?? 0) > 0,
    progress: (s) => coverage(s.describedCount, s.imageCount),
  },
  {
    key: 'specs',
    pillar: 'everyPieceDescribed',
    weight: 15,
    label: () => 'Record medium and size — the two things you always have to look up',
    evidence:
      'Medium and dimensions are the first questions asked of any piece, by a gallery, a buyer or a submission form.',
    why: 'These are the details that live in your head or on the back of the canvas, and the ones you have to go and measure again every time someone asks. Recording them once ends that.',
    href: '/art',
    opens: 'explainer',
    minutes: 5,
    requires: (s) => (s.imageCount ?? 0) > 0,
    progress: (s) => coverage(s.specsCount, s.imageCount),
  },
  {
    key: 'collections',
    pillar: 'grouped',
    weight: 20,
    label: (s) => `Group your work into collections — ${Math.min(s.collectionCount ?? 0, 3)} of 3`,
    evidence: 'A body of work reads as a body of work when it is grouped. A flat grid reads as a camera roll.',
    why: 'Collections are how you show a series as a series, and how you send one coherent group to a gallery or a client instead of everything you have ever made.',
    href: '/my-artwork',
    opens: 'explainer',
    progress: (s) => ratio(s.collectionCount, 3),
  },
  {
    key: 'profileFields',
    pillar: 'presentable',
    weight: 20,
    label: () => 'Finish your profile — so the portfolio is something you can send',
    evidence: 'Your profile is the page a portfolio link opens on.',
    why: 'An organised portfolio is only useful if you can hand someone the link. This is the page that link opens, and the difference between sending it and apologising for it.',
    href: '/profile',
    opens: 'new-tab',
    minutes: 5,
    progress: (s) => profileCompleteness(s.profile ?? null).score / 100,
  },
];

export const INTENT_VARIANTS: Record<IntentVariantKey, IntentVariant> = {
  sell: {
    key: 'sell',
    heading: 'A few steps to selling more art',
    gatePillar: 'bodyOfWork',
    gateRequirement: `${BODY_OF_WORK_BAR} pieces, reviewed and priced`,
    pillarLabels: SELL_PILLAR_LABELS,
    pillarOrder: SELL_PILLAR_ORDER,
    steps: SELL_STEPS,
  },
  seen: {
    key: 'seen',
    heading: 'A few steps to getting your art seen',
    gatePillar: 'channels',
    gateRequirement: 'a connected channel',
    pillarLabels: {
      enoughToPost: `Upload ${BODY_OF_WORK_BAR}+ pieces so you never run out of things to post`,
      postReady: 'Make your work stop the scroll instead of blending in',
      channels: 'Connect where your art gets seen by people who are not here',
      cadence: 'Automated Exposure - get eyeballs to see your art',
    },
    pillarOrder: ['enoughToPost', 'postReady', 'channels', 'cadence'],
    steps: SEEN_STEPS,
  },
  better: {
    key: 'better',
    heading: 'A few steps to better feedback on your work',
    gatePillar: 'askForFeedback',
    gateRequirement: 'a critique post',
    pillarLabels: {
      workToShow: 'Upload enough work that a critique can see a direction',
      askForFeedback: 'Ask for the feedback you actually want on a piece',
      giveFeedback: 'Critique other artists so they turn up for yours',
      yourPeople: 'Build a feed of artists worth learning from',
    },
    pillarOrder: ['workToShow', 'askForFeedback', 'giveFeedback', 'yourPeople'],
    steps: BETTER_STEPS,
  },
  organize: {
    key: 'organize',
    heading: 'A few steps to a portfolio you can actually use',
    gatePillar: 'everyPieceDescribed',
    gateRequirement: 'described work',
    pillarLabels: {
      everythingUploaded: 'Get every piece you own into one place you trust',
      everyPieceDescribed: 'Give each piece the details you always have to look up',
      grouped: 'Group your work so it reads as a body, not a camera roll',
      presentable: 'Make the portfolio something you can send to anyone',
    },
    pillarOrder: ['everythingUploaded', 'everyPieceDescribed', 'grouped', 'presentable'],
    steps: ORGANIZE_STEPS,
  },
};

/**
 * Which variant an artist gets, from the intents she picked.
 *
 * SELL WINS WHENEVER IT IS PRESENT. It is the intent with the most commercial weight and
 * the one least likely to have been picked idly, and 62.6% of answers include it. After
 * that a fixed order decides, so the same answers always produce the same band.
 *
 * `onboardingIntent` values are ADMIN-EDITABLE FREE STRINGS, not an enum — the API's own
 * pre-feed service says so. An intent nobody has mapped yet, an empty array and a null all
 * fall through to `sell`, which is the default band and the largest audience. A new option
 * added in the admin tomorrow degrades to the default rather than throwing.
 */
const INTENT_TO_VARIANT: Record<string, IntentVariantKey> = {
  sell: 'sell',
  social: 'seen',
  feedback: 'better',
  connect: 'better',
  learn: 'better',
  inspire: 'better',
  organize: 'organize',
};

/** Fixed order for the artists who did not pick `sell`, most-specific first. */
const VARIANT_PRIORITY: readonly IntentVariantKey[] = ['sell', 'seen', 'better', 'organize'];

export function variantForIntents(intents: readonly string[] | null | undefined): IntentVariantKey {
  if (!intents?.length) return 'sell';
  const mapped = new Set(intents.map((intent) => INTENT_TO_VARIANT[intent]).filter(Boolean) as IntentVariantKey[]);
  return VARIANT_PRIORITY.find((candidate) => mapped.has(candidate)) ?? 'sell';
}

/** Total weight per pillar, for one variant. Derived, so it can never disagree with the steps. */
export function pillarWeights(variant: IntentVariant): Record<ArtistStandardPillarKey, number> {
  return variant.steps.reduce<Record<ArtistStandardPillarKey, number>>((memo, step) => {
    memo[step.pillar] = (memo[step.pillar] ?? 0) + step.weight;
    return memo;
  }, {});
}

/** The score a fully-standard artist earns. Asserted per variant in the spec. */
export const ARTIST_STANDARD_MAX = 100;

export type ArtistStandardResult = {
  /** Which variant produced this. Scores are NOT comparable across variants. */
  variant: IntentVariantKey;
  /** The band's headline for this variant. */
  heading: string;
  /** 0-100. The sum of the earned weights. */
  score: number;
  /**
   * Every step in GUIDED-PATH ORDER — the order the steps are declared in, which is the
   * order she should work through them.
   *
   * Not the same as flattening `pillars`, and that difference is a bug waiting to happen:
   * flattening regroups the steps under their pillars, so an easy-first path silently
   * comes back out in pillar order. A surface listing "what to do next, and then what"
   * wants THIS; a surface breaking the score down wants `pillars`.
   */
  steps: ArtistStandardStep[];
  pillars: ArtistStandardPillar[];
  /** Rung name on each ladder, so a surface can render either without rescoring. */
  rung: Record<RungLadder, string>;
  /** The rung above the current one — `null` at the top. */
  nextRung: Record<RungLadder, string | null>;
  /** The single most valuable open step — `null` only when everything is done. */
  nextStep: ArtistStandardStep | null;
  /** True once every step is complete: the meter has done its job and must disappear. */
  meetsStandard: boolean;
  /** True when the rung is being held down by the variant's gate rather than the score. */
  cappedByBodyOfWork: boolean;
  /** The requirement the gate note names — per-variant, not sell-only. */
  gateRequirement: string;
};

function rungIndex(score: number, ladder: RungLadder, capped: boolean): number {
  const floors = RUNG_FLOORS[ladder];
  const reached = floors.reduce((memo, floor, index) => (score >= floor ? index : memo), 0);
  if (!capped) return reached;
  return Math.min(reached, RUNG_CAP_WITHOUT_GATE);
}

/**
 * Scores an artist against the standard and names the one thing to do next.
 *
 * Earned weights are rounded per step rather than at the end, so the sum a UI adds up
 * from the pillar rows is the same number as `score` — a total that disagrees with its
 * own visible parts reads as a bug to the artist looking at it.
 */
export function artistStandard(
  subject: ArtistStandardSubject | null | undefined,
  /**
   * The variant, or the intents to pick one from. Defaults to `sell` — the largest
   * audience and the band every surface rendered before variants existed, so an existing
   * caller keeps its behaviour without being changed.
   */
  options?: { variant?: IntentVariantKey; intents?: readonly string[] | null }
): ArtistStandardResult {
  const input = subject ?? {};
  const variantKey = options?.variant ?? variantForIntents(options?.intents);
  const variant = INTENT_VARIANTS[variantKey] ?? INTENT_VARIANTS.sell;
  const weights = pillarWeights(variant);

  const steps: ArtistStandardStep[] = variant.steps.map((spec) => {
    const progress = Math.max(0, Math.min(1, spec.progress(input)));
    const complete = progress >= 1;
    const rounded = Math.round(spec.weight * progress);
    // Rounding a near-full step (97/100 × 15 = 14.55) would otherwise print
    // the full weight while `complete` stays false — a 15/15 bar with Go still open.
    const earned = complete ? spec.weight : Math.min(spec.weight - 1, rounded);
    return {
      key: spec.key,
      pillar: spec.pillar,
      weight: spec.weight,
      earned,
      complete,
      label: spec.label(input),
      evidence: spec.evidence,
      why: spec.why,
      opens: spec.opens,
      href: spec.href,
      ...(spec.aspirational ? { aspirational: true } : {}),
      ...(spec.minutes ? { minutes: spec.minutes } : {}),
      ...(spec.requires ? { requires: spec.requires } : {}),
    };
  });

  const score = steps.reduce((memo, step) => memo + step.earned, 0);

  const pillars: ArtistStandardPillar[] = variant.pillarOrder.map((key) => {
    const own = steps.filter((step) => step.pillar === key);
    return {
      key,
      label: variant.pillarLabels[key] ?? key,
      weight: weights[key] ?? 0,
      earned: own.reduce((memo, step) => memo + step.earned, 0),
      complete: own.every((step) => step.complete),
      steps: own,
    };
  });

  // The gate is per-variant: the one pillar the top rung cannot be reached without. For a
  // seller that is the body of work; for someone chasing exposure it is having a channel
  // connected, because nothing else here reaches anyone until one is.
  const gateComplete = pillars.find((pillar) => pillar.key === variant.gatePillar)?.complete ?? false;
  const capped = !gateComplete;

  const rung = Object.fromEntries(
    (['standard', 'career'] as const).map((ladder) => {
      const names = RUNG_NAMES[ladder];
      return [ladder, names[rungIndex(score, ladder, capped)]];
    })
  ) as Record<RungLadder, string>;
  const nextRung = Object.fromEntries(
    (['standard', 'career'] as const).map((ladder) => {
      const names = RUNG_NAMES[ladder];
      return [ladder, names[rungIndex(score, ladder, capped) + 1] ?? null];
    })
  ) as Record<RungLadder, string | null>;

  // The next action has to be something she can actually START. A blocked step keeps its
  // place in the list and its weight in the score; it just cannot be the thing we point at.
  const openSteps = steps.filter((step) => !step.complete);
  const nextStep = openSteps.find((step) => step.requires?.(input) ?? true) ?? openSteps[0] ?? null;

  return {
    variant: variantKey,
    heading: variant.heading,
    score,
    steps,
    pillars,
    rung,
    nextRung,
    nextStep,
    meetsStandard: nextStep === null,
    cappedByBodyOfWork: capped && score >= (RUNG_FLOORS.standard[RUNG_CAP_WITHOUT_GATE + 1] ?? Infinity),
    gateRequirement: variant.gateRequirement,
  };
}
